import React, { useCallback, useEffect, useRef } from 'react';

import useOnScreen from '@exp/hooks/useOnScreen';
import RecommendationStars from '@exp/components/recommendations/RecommendationStars';

import { InitialFormModes } from '../constants/constants';
import backpack from '../images/vets/backpack.png';
import benchmade from '../images/vets/benchmade.png';
import eberlestock from '../images/vets/eberlestock.png';
import fiveeleven from '../images/vets/511.png';
import logo from '../images/vets/logo.svg';
import logoColor from '../images/vets/logoColor.png';
import mammut from '../images/vets/mammut.png';
import manscope from '../images/vets/manscope.png';
import outdoorresearch from '../images/vets/outdoorresearch.png';
import piggy from '../images/vets/piggy.svg';
import quoteDark from '../images/vets/quotedark.svg';
import quoteLight from '../images/vets/quotelight.svg';
import quoteYellow from '../images/vets/quoteYellow.svg';
import scope from '../images/vets/scope.png';
import search from '../images/vets/search.svg';
import shield from '../images/vets/shield.svg';
import stanley from '../images/vets/stanley.png';
import sunglasses from '../images/vets/sunglasses.png';
import { setFormMode } from '../redux/context';
import { useAppDispatch } from '../redux/hooks';

import './VetsBackdrop.less';

// Not translating any of these messages, as it's a US only thing and a temporary page.
const VetsBackdrop = () => {
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLElement | null>(null);
  const scrollingToForm = useRef(false);

  useEffect(() => {
    formRef.current = window.document.getElementById('exp-onb-initial-form');
  }, []);

  const formInView = useOnScreen(formRef, { threshold: 1.0 });

  const handleScrollToElement = useCallback(() => {
    formRef.current = window.document.getElementById('exp-onb-initial-form');
    if (formRef.current) {
      scrollingToForm.current = true;
      formRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    }

    dispatch(setFormMode(InitialFormModes.SIGN_UP));
  }, [dispatch]);

  useEffect(() => {
    // Auto-focus the username (or firstName) field if the form is being scrolled into view
    if (formInView && scrollingToForm.current) {
      let input = window.document.getElementById('exp-onb-username');
      if (!input) {
        input = window.document.getElementById('exp-onb-first-name');
      }

      input?.focus();
      scrollingToForm.current = false;
    }
  }, [formInView]);

  return (
    <div className="vetsBackdrop">
      <header className="vets-header">
        <div className="header-inset">
          <div className="header-content">
            <h4>
              <img src={logo} alt="U.S. Military Veterans" />
              U.S. Military Veterans
            </h4>
            <h1>
              Get exclusive access to
              <span>over 650 brands trusted</span>
              by the military community.
            </h1>
            <div className="quote-block">
              <img src={quoteLight} alt="“" />
              <div className="quote-copy">
                <p className="quote">
                  Great products at great prices, anything from sunglasses to calf compression socks.
                </p>
                <p>
                  <span className="author">Steven H.</span>
                  <span className="team">, Military & Veterans</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>
      <div className="brands-tease">
        <h3>The brands you know. The prices you deserve.</h3>
        <h5>
          Over
          <strong> 650 brands with exclusive military discounts </strong>
          you won&lsquo;t find anywhere else.
        </h5>
        <div className="brands">
          <img src={benchmade} alt="Benchmade" />
          <img src={mammut} alt="Mammut" />
          <img src={fiveeleven} alt="Fiveeleven" />
          <img src={stanley} alt="Stanley" />
          <img src={outdoorresearch} alt="Outdoor Research" />
          <img src={eberlestock} alt="Eberlestock" />
        </div>
      </div>
      <div className="reviews-tease">
        <h3>Trusted by those who’ve served</h3>
        <h5>Join experts who test, review and share real experiences.</h5>
        <div className="reviews">
          <div className="proof-guy">
            <img className="author-pic" src={manscope} alt="Veteran with scope" />
            <p className="author">Josh Bessette, U.S. Military Veteran</p>
            <div className="quote-block">
              <img src={quoteDark} alt="" />
              <div className="quote-copy">
                The reviews on ExpertVoice are a huge help in selecting the right gear. Many sites have bot reviews, or
                the review never goes beyond the unboxing. I love having a platform to see the gear in use, especially
                hard use.
              </div>
            </div>
          </div>
          <div className="review-samples">
            <div className="reviewBlock">
              <div className="review">
                <img src={sunglasses} alt="Sunglasses" />
                <div className="review-details">
                  <RecommendationStars starRating={5} />
                  <p className="review-text">
                    <span className="product">Costa Sunglasses.</span>
                    Game changer for fishing. Cut glare, boosted visibility, and kept us comfortable in tough sun.
                    Must-have for serious time on the water.
                  </p>
                  <p className="author">
                    <i className="exp-ux-mini exp-ux-bolt" />
                    Adam B., U.S. Military Veterans
                  </p>
                  <p className="review-text">
                    <span className="product">Costa</span>
                    RINCON
                  </p>
                </div>
              </div>
              <div className="review">
                <img src={scope} alt="Scope" />
                <div className="review-details">
                  <RecommendationStars starRating={5} />
                  <p className="review-text">
                    <span className="product">Athlon Argos BTR GEN2 Scope.</span>
                    Crystal-clear optics that hold zero reliably—even after lots of rounds.
                  </p>
                  <p className="author">
                    <i className="exp-ux-mini exp-ux-bolt" />
                    Lisa A., U.S. Military Veterans
                  </p>
                  <p className="review-text">
                    <span className="product">Athlon Optics</span>
                    Argos BTR GEN2 6-24X50, FFP, APLR2 IR - MOA
                  </p>
                  <p className="discount-pill">Save 55%</p>
                </div>
              </div>
              <div className="review">
                <img src={backpack} alt="Backpack" />
                <div className="review-details">
                  <RecommendationStars starRating={5} />
                  <p className="review-text">
                    <span className="product">Everyday Carry Bag.</span>
                    Tough, compact, and organized. Great daily bag with smart compartments and perfect for concealed
                    carry. Still going strong after years.
                  </p>
                  <p className="author">
                    <i className="exp-ux-mini exp-ux-bolt" />
                    Robert K., U.S. Military Veterans
                  </p>
                  <p className="review-text">
                    <span className="product">5.11</span>
                    RUSH Moab 10 Sling Pack 18L
                  </p>
                  <p className="discount-pill">Save 40%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="how-tease">
        <div className="how">
          <h3>How it works</h3>
          <div className="teasers">
            <div className="how-tease">
              <img src={shield} alt="Verify your service" />
              <h4>Verify your service</h4>
              <p>
                Sign up and securely share proof of your veteran status with a DD-214, VA card, endorsed driver&apos;s
                license or official military status letter.
              </p>
            </div>
            <div className="how-tease">
              <img src={search} alt="Explore exclusive access" />
              <h4>Explore exclusive access</h4>
              <p>
                Tactical gear, outdoor equipment, footwear and more — from 650+ trusted brands with exclusive insights
                and authentic reviews.
              </p>
            </div>
            <div className="how-tease">
              <img src={piggy} alt="Save up to 60%" />
              <h4>Save up to 60%</h4>
              <p>
                Fresh deals drop every week — including Flash Deals and extra savings events. Plus, get the deepest
                discounts in the Outlet.
              </p>
            </div>
          </div>
          <button className="btn-primary" onClick={handleScrollToElement} type="button">
            Ready to Get Started?
          </button>
        </div>
      </div>
      <div className="quotes">
        <div className="quotes-container">
          <div className="quote-block">
            <img src={quoteYellow} alt="“" />
            <div className="quote-copy">
              <p className="quote">
                I really value the chance to engage with reputable brands. Using the products myself helps me share
                genuine, informed feedback.
              </p>
              <p className="author">Jacob S.</p>
              <p className="team">Military & Veterans</p>
            </div>
          </div>
          <div className="quote-block">
            <img src={quoteYellow} alt="“" />
            <div className="quote-copy">
              <p className="quote">
                ExpertVoice helps me get great deals and speak confidently about products when people ask.
              </p>
              <p className="author">Catherine L.</p>
              <p className="team">Military & Veterans</p>
            </div>
          </div>
          <div className="quote-block">
            <img src={quoteYellow} alt="“" />
            <div className="quote-copy">
              <p className="quote">
                Love the wide brand variety, stackable deals with Outlet and Flash, and expert gear reviews before
                buying.
              </p>
              <p className="author">Scott H.</p>
              <p className="team">Military & Veterans</p>
              <p className="team">Public Land & Wildlife Management</p>
            </div>
          </div>
          <div className="quote-block">
            <img src={quoteYellow} alt="“" />
            <div className="quote-copy">
              <p className="quote">
                I keep coming back for the constant stream of new brands and unbeatable discounts.
              </p>
              <p className="author">Bob G.</p>
              <p className="team">Military & Veterans</p>
              <p className="team">Golf Professionals & Retail</p>
            </div>
          </div>
          <div className="quote-block">
            <img src={quoteYellow} alt="“" />
            <div className="quote-copy">
              <p className="quote">Love the exclusive discounts just for sharing feedback—this site is awesome!</p>
              <p className="author">Miguel A.</p>
              <p className="team">Golf Professionals & Retail</p>
              <p className="team">Military & Veterans</p>
            </div>
          </div>
          <div className="quote-block">
            <img src={quoteYellow} alt="“" />
            <div className="quote-copy">
              <p className="quote">
                I love how convenient it is. The deals are great and the brands are legit — I always look forward to
                checking what’s new.
              </p>
              <p className="author">James H.</p>
              <p className="team">Military & Veterans</p>
            </div>
          </div>
          <div className="quote-block">
            <img src={quoteYellow} alt="“" />
            <div className="quote-copy">
              <p className="quote">
                It’s a great way to discover new gear — and you’re hearing from people who actually know what they’re
                talking about.
              </p>
              <p className="author">Zach H.</p>
              <p className="team">Military & Veterans</p>
            </div>
          </div>
          <div className="quote-block">
            <img src={quoteYellow} alt="“" />
            <div className="quote-copy">
              <p className="quote">The great deals, and the amount of brands make it worthwhile.</p>
              <p className="author">Travis M.</p>
              <p className="team">Military & Veterans</p>
            </div>
          </div>
          <div className="quote-block">
            <img src={quoteYellow} alt="“" />
            <div className="quote-copy">
              <p className="quote">This place has some awesome brands at crazy prices!</p>
              <p className="author">Nicholas F.</p>
              <p className="team">Military & Veterans</p>
            </div>
          </div>
        </div>
        <p className="quote-subtext">
          <i className="exp-ux-small exp-ux-shield" />
          Trusted by thousands of verified veterans and active duty members
        </p>
      </div>
      <div className="raffle">
        <div className="raffle-card">
          <img src={logoColor} alt="U.S. Military Veterans" />
          <h2>You’ve earned more than thanks</h2>
          <p>
            Join ExpertVoice and get access to discounts from 650+ brands like Stanley, Benchmade, Outdoor Research,
            Mammut and JBL—all in one place.
          </p>
          <button className="btn-primary" type="button" onClick={handleScrollToElement}>
            Join Now
          </button>
        </div>
      </div>
    </div>
  );
};

VetsBackdrop.displayName = 'VetsBackdrop';

export default VetsBackdrop;
